import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { useTranslation } from 'react-i18next';

interface ImageContextMenuProps {
  open: boolean;
  onClose: () => void;
  anchorPosition: { top: number; left: number } | null;
  onPreview: () => void;
  onEdit: () => void;
  onMove: () => void;
  onDownload: () => void;
  onInfo: () => void;
  onDelete: () => void;
  imageName: string;
}

const ImageContextMenu: React.FC<ImageContextMenuProps> = ({
  open,
  onClose,
  anchorPosition,
  onPreview,
  onEdit,
  onMove,
  onDownload,
  onInfo,
  onDelete,
  imageName
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ? anchorPosition : undefined}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        elevation: 8,
        sx: {
          minWidth: 200,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          '& .MuiMenuItem-root': {
            borderRadius: 1,
            margin: '2px 4px',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
        }
      }}
      disableRestoreFocus
    >
      <MenuItem disabled sx={{ fontWeight: 'bold', opacity: 1, color: 'primary.main' }}>
        <ListItemIcon>
          <ImageIcon color="primary" />
        </ListItemIcon>
        <ListItemText 
          primary={imageName} 
          primaryTypographyProps={{ 
            variant: 'subtitle2', 
            noWrap: true,
            sx: { maxWidth: 140 }
          }} 
        />
      </MenuItem>
      
      <Divider sx={{ my: 1 }} />
      
      <MenuItem onClick={onPreview}>
        <ListItemIcon>
          <VisibilityIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={tt('Önizle', 'Preview')} />
      </MenuItem>
      
      <MenuItem onClick={onDownload}>
        <ListItemIcon>
          <DownloadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={tt('İndir', 'Download')} />
      </MenuItem>
      
      <Divider sx={{ my: 1 }} />
      
      <MenuItem onClick={onEdit}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={tt('Adını Değiştir', 'Rename')} />
      </MenuItem>
      
      <MenuItem onClick={onMove}>
        <ListItemIcon>
          <DriveFileMoveIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={tt('Taşı', 'Move')} />
      </MenuItem>
      
      <MenuItem onClick={onInfo}>
        <ListItemIcon>
          <InfoIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={tt('Bilgiler', 'Info')} />
      </MenuItem>
      
      <Divider sx={{ my: 1 }} />
      
      <MenuItem 
        onClick={onDelete}
        sx={{ 
          color: 'error.main',
          '&:hover': {
            backgroundColor: 'error.light',
            color: 'error.contrastText',
          }
        }}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText primary={tt('Sil', 'Delete')} />
      </MenuItem>
    </Menu>
  );
};

export default ImageContextMenu; 